'use strict';

/**
 * Soracom Authentication AuthKeys Module
 * Handles AuthKeys-specific authentication flows and UI management
 */
window.SoracomAuthKeys = (() => {

  /**
   * Try AuthKeys endpoint for authentication
   * @param {string} endpoint - API endpoint
   * @param {string} authKeyId - AuthKey ID
   * @param {string} authKeySecret - AuthKey Secret
   * @param {string} coverage - Coverage type
   * @returns {Promise<Object>} Authentication result
   */
  async function tryAuthKeysEndpoint(endpoint, authKeyId, authKeySecret, coverage) {
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          authKeyId: authKeyId,
          authKey: authKeySecret
        })
      });

      if (!response.ok) {
        if (window.SoracomAuth?.showAuthError) {
          if (response.status === 401) {
            window.SoracomAuth.showAuthError('Authentication failed. Please check your AuthKey credentials and try again.');
          } else {
            window.SoracomAuth.showAuthError(`Authentication failed with status ${response.status}. Please try again.`);
          }
        }
        return { success: false, coverage, error: `HTTP ${response.status}` };
      }

      const data = await response.json();

      if (data.apiKey && data.token) {
        return {
          success: true,
          coverage,
          apiKey: data.apiKey,
          token: data.token,
          operatorId: data.operatorId,
          endpoint: endpoint.replace('/v1/auth', '')
        };
      }

      if (window.SoracomAuth?.showAuthError) {
        window.SoracomAuth.showAuthError('Authentication failed: Invalid response from server.');
      }
      return { success: false, coverage, error: 'Invalid response' };
    } catch (err) {
      const sanitizedError = window.SoracomAuthUtils?.sanitizeForLogging?.(err?.message || err) || '[error details unavailable]';
      console.error('AuthKeys authentication error:', sanitizedError);

      if (window.SoracomAuth?.showAuthError) {
        if (err.name === 'TypeError' && err.message.includes('Failed to fetch')) {
          window.SoracomAuth.showAuthError('Network error. Please check your connection.');
        } else {
          window.SoracomAuth.showAuthError('Authentication failed. Please try again.');
        }
      }

      return { success: false, coverage, error: err.message };
    }
  }

  /**
   * Store AuthKeys authentication data
   * @param {Object} result - Authentication result
   * @param {HTMLElement} authButton - Auth button element
   */
  function storeAuthKeysData(result, authButton) {
    const authData = {
      apiKey: result.apiKey,
      token: result.token,
      authType: 'authkeys',
      coverage: result.coverage,
      endpoint: result.endpoint
    };

    // Only include operatorId if it's actually provided by the API
    if (result.operatorId) {
      authData.operatorId = result.operatorId;
    }

    if (!window.SoracomAuthEncryption?.validateAuthData(authData)) {
      console.error('AuthKeys data validation failed');
      resetAuthKeysButton(authButton);
      return;
    }

    window.SoracomAuth?.storeAuth(authData);

    // Clear temp AuthKeys since SSO takes precedence
    sessionStorage.removeItem('auth_redirect');

    // Set flag to indicate this is immediately after authentication
    sessionStorage.setItem('authkeys_just_authenticated', 'true');
    // Clear the flag after a short delay so subsequent page loads show placeholders
    setTimeout(() => {
      sessionStorage.removeItem('authkeys_just_authenticated');
    }, 1000);

    if (authButton) {
      authButton.textContent = 'Sign Out';
      authButton.disabled = false;
      authButton.style.removeProperty('background-color');
      authButton.style.removeProperty('border-color');
      authButton.style.cursor = '';
      authButton.classList.add('sign-out-btn');
      authButton.classList.add('auth-primary-button');
    }

    // Update SSO button to show it's not the active auth method
    const ssoSignInButton = document.getElementById('sso-sign-in');
    const ssoSignOutButton = document.getElementById('sso-sign-out');
    if (ssoSignInButton) {
      ssoSignInButton.style.display = 'block';
      ssoSignInButton.disabled = false;
    }
    if (ssoSignOutButton) {
      ssoSignOutButton.style.display = 'none';
    }

    window.SoracomAuth?.checkAuthAndUpdateUI();

    // Close coverage panel after successful authentication
    setTimeout(() => {
      const coveragePanel = document.getElementById('coverage-panel');
      const coverageMenu = document.getElementById('coverage-menu');
      if (coveragePanel) {
        coveragePanel.classList.remove('open');
      }
      if (coverageMenu) {
        coverageMenu.classList.remove('open');
        coverageMenu.classList.remove('active');
      }
    }, 100);
    window.SoracomAuthUtils?.updateCopyButtonsState();
  }

  /**
   * Reset AuthKeys button to default state
   * @param {HTMLElement} authButton - Auth button element
   */
  function resetAuthKeysButton(authButton) {
    if (authButton) {
      authButton.textContent = 'Authenticate';
      authButton.disabled = false;
      authButton.style.removeProperty('background-color');
      authButton.style.removeProperty('border-color');
      authButton.style.cursor = '';
      authButton.classList.remove('sign-out-btn');
      authButton.classList.add('auth-primary-button');
    }
  }

  /**
   * Flash AuthKey input error
   */
  function flashAuthKeyInputError() {
    const idInput = document.getElementById('authKeyId');
    const secretInput = document.getElementById('authKeySecret');
    if (idInput) idInput.classList.add('input-error');
    if (secretInput) secretInput.classList.add('input-error');

    setTimeout(() => {
      if (idInput) idInput.classList.remove('input-error');
      if (secretInput) secretInput.classList.remove('input-error');
    }, 800);
  }

  // Public API
  return {
    tryAuthKeysEndpoint,
    storeAuthKeysData,
    resetAuthKeysButton,
    flashAuthKeyInputError
  };

})();
